import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@/services/axios-base-query';
import { COURSE_ROUTES, USER_ROUTES } from '@/constants';
import type {
    Course,
    Board,
    BoardTreeNode,
    BoardTeacher,
    CreateBoardPayload,
    CreateCoursePayload,
    CreateCourseSectionPayload,
    PatchCoursePayload,
    PatchCourseSectionPayload,
    CourseSection,
    CourseTeacher,
    EnrolledStudent,
    EnrollStudentAttendance,
    EnrollStudentsPayload,
    StudentAttendanceRecord,
    LeaveRequestRecord,
    CreateAttendancePayload,
    UpdateAttendancePayload,
    AddCourseTeachersPayload,
    AddBoardTeachersPayload,
    CourseSectionRepoItem,
} from '@/types/api/course.types';
import type { User } from '@/types';

export interface CourseMarkingSchemeItem {
    id: number;
    marking_structure_name: string;
    description: string | null;
    course_percentage: number;
    full_marks: number;
    course: number;
    course_structure: {
        id: number;
        assessment_type: string;
        description: string;
        created_at: string;
        updated_at: string;
    };
    course_sub_structure: {
        id: number;
        structure: number;
        sub_structure_type: string;
        description: string;
        created_at: string;
        updated_at: string;
    } | null;
}

export interface CourseStructureItem {
    id: number;
    assessment_type: string;
    description: string;
    created_at: string;
    updated_at: string;
}

export interface AssignmentSkillItem {
    id: number;
    skill_name: string;
    required_level: number;
    assignments: number;
    skills: number;
}

export interface AssignmentItem {
    id: number;
    assignment_repo: CourseSectionRepoItem[];
    assignments_skills: AssignmentSkillItem[];
    assignment_type: 'INDIVIDUAL' | 'GROUP' | string;
    assignment_name: string;
    assignment_brief: string | null;
    assignment_weightage: number;
    submission_deadline: string;
    marking_scheme: {
        full_marks: number;
        course_percentage: number;
        course: number;
        id: number;
        course_structure: number;
        course_sub_structure: number | null;
        marking_structure_name: string;
        description: string | null;
    } | null;
    course: number;
    course_section: number | null;
}

export interface GenerateAssignmentIdeasResponse {
    assignments: {
        title: string;
        description: string;
        learning_objectives?: { objective: string; category: string }[];
        deliverables?: { item: string; type: string; description: string }[];
        skills?: { skill: string; level: string }[];
        suggested_timeline?: string;
        complexity?: string;
    }[];
}

export interface ProcessCourseDocumentResponse {
    success: boolean;
    message: string;
    data: {
        thread_id: string;
        course_name: string;
        course_code: string;
        level: string;
        number_of_chapters: number;
        board: string;
        lecturer: string;
        full_marks: number;
        chapters: { title: string }[];
        course_summary: string;
        learning_objectives: string[];
    };
}

export interface GenerateStudyMaterialsResponse {
    success: boolean;
    message: string;
    data: {
        thread_id: string;
        study_materials: {
            section_name: string;
            learning_objectives: string[];
            prerequisites: string[];
            section_overview: string;
            detailed_explanations: string;
        }[];
    };
}

export interface EvaluateCoursePayload {
    students: Array<{
        evaluation_points: number;
        user_id: string;
        user_skills: number;
        feedback: string;
        evaluation_assignment: number;
        evaluation_course: number;
        course_section: number | null;
    }>;
}

export interface SubmissionUser {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    photo_url: string | null;
}

export interface StudentSubmissionItem {
    id: number;
    student_submission: SubmissionFeedbackItem[];
    submission_type: number;
    assignment_file: string | null;
    assignment_details: string | null;
    submission_date: string;
    assignment: number;
    submitted_by: SubmissionUser;
    submission_group: string | null;
}

export interface SubmissionFeedbackItem {
    id: number;
    feedback: string | null;
    marking: number;
    quiz: number | null;
    student_submission: number;
    feedback_provided_by: SubmissionUser;
}

export interface CreateCourseFeedbackPayload {
    feedback: string;
    rating: number;
    anonymous: boolean;
    course: number;
}

export interface CourseFeedbackItem {
    id: number;
    feedback: string;
    rating: string;
    created_date: string;
    anonymous: boolean;
    hidden: boolean;
    course: number;
    user: SubmissionUser | null;
}

export interface GradeStudentAssignmentResponse {
    suggested_grade: number;
    summary: string;
    strengths: string[];
    weaknesses: string[];
    feedback: unknown;
    summary_lexical: unknown;
    strengths_lexical: unknown;
    weaknesses_lexical: unknown;
}

export interface CreateStudentGroupPayload {
    group_name: string;
    assignment: number;
    users: string[];
    group_leader: string;
}

export interface PatchStudentGroupPayload {
    users?: string[];
    group_leader?: string;
    group_name?: string;
}

export interface StudentGroupUser {
    name: string;
    id: string;
    email: string;
    photo_url: string | null;
}

export interface StudentGroupItem {
    id: number;
    submission: StudentSubmissionItem[];
    group_name: string;
    created_date: string;
    assignment: number;
    group_leader: StudentGroupUser;
    users: StudentGroupUser[];
}

export interface CourseAcquiredMarksItem {
    id: number;
    marks: number;
    full_marks: number;
    grade: string | null;
    status: number;
    created_at: string;
    updated_at: string;
    marking_scheme: number;
    course: number;
    student_enroll: number;
    student: string;
    created_by: string;
}

export interface MultipleCourseAcquiredMarksPayloadItem {
    marks: number | null;
    full_marks: number | null;
    grade: string | null;
    status: number | null;
    marking_scheme: number | null;
    course: number | null;
    student_enroll: number | null;
    student: string | null;
    id?: number;
}

export interface MultipleCourseAcquiredMarksPayload {
    students: MultipleCourseAcquiredMarksPayloadItem[];
}

export interface GradingStructureRangeItem {
    id: number;
    lower_threshold: number;
    upper_threshold: number;
    grade: string;
    grading_structure: number;
}

export interface GradingStructureSchemeItem {
    id: number;
    grading_structure: GradingStructureRangeItem[];
    grading_structure_name: string;
    created_date: string;
    updated_date: string;
    created_by: string;
}

export interface CourseAnalyticsUserProfile {
    id: string;
    email: string;
    created_on: string;
    roles: string[] | null;
    last_login: string | null;
    profile: {
        id: number;
        profile_setup: boolean;
        address: string | null;
        photo_url: string | null;
        thumbnail: string | null;
        first_name: string;
        last_name: string;
        title: string | null;
        about_me: string | null;
        created_on: string;
        updated_on: string;
        gender: number | null;
    } | null;
}

export interface StudentCourseEvaluationReportItem {
    id: number;
    self_evaluation: string;
    user: string;
    skill: {
        id: number;
        name: string;
    };
    skill_interest: string;
    total_evaluated_points: number;
}

export interface CourseSkillEvaluationListingItem {
    skill_name: string;
    feedback: string;
    evaluated_as_display: string | null;
    user: {
        id: string;
        email: string;
    };
    evaluation_points: number;
    evaluator_schema: string;
    evaluated_by_email: string;
    evaluation_course: number;
    evaluation_assignment: number;
    evaluation_submission: number | null;
    skill_id: number;
    anonymous_evaluation: boolean;
    evaluator_first_name: string;
    evaluator_last_name: string;
    evaluator_id: string;
    user_evaluated_by_email: string;
}

export interface StudentAssignmentReportItem {
    id: number;
    total_assignments: number;
    submitted_assignments: number;
    not_submitted_assignments: number;
    graded_assignments: number;
    active: boolean;
    enrolled_date: string;
    board: number | null;
    semester_course: number;
    student: string;
}

export interface StudentYearlyAttendanceReportItem {
    attendance_status: number | null;
    day: string;
    present: number;
    absent: number;
    leave_request_status: number | null;
    ratio: number;
}

export interface StudentAttendancePercentageSummary {
    total_working_days: number;
    total_present_days: number;
    total_absent_days: number;
    attendance_ratio: number;
}

export interface CourseReportStudentItem {
    id: number;
    marks: number;
    full_marks: number;
    acquired_percentage: number;
    student: SubmissionUser | null;
}

export interface CourseReportItem {
    id: number;
    marking_structure_name: string;
    description: string | null;
    course_percentage: number;
    full_marks: number;
    course: number;
    course_structure: number;
    course_sub_structure: number | null;
    created_at: string;
    created_by: string;
    student: CourseReportStudentItem[];
}

export const courseApi = createApi({
    reducerPath: 'CourseApi',
    baseQuery: axiosBaseQuery(),
    tagTypes: [
        'Courses',
        'Boards',
        'Users',
        'CourseDetail',
        'CourseTeachers',
        'BoardPeople',
        'CourseSections',
        'CourseEnrollments',
        'CourseAttendance',
        'CourseAssignments',
        'CourseFeedbacks',
        'CourseGradingSchemes',
    ],
    endpoints: (builder) => ({
        getCourses: builder.query<Course[], { tenant: string; board?: number }>({
            query: ({ tenant, board }) => {
                return {
                    url: COURSE_ROUTES.GET_COURSES(tenant),
                    method: 'GET',
                    params: board ? { board } : {},
                };
            },
            providesTags: ['Courses'],
        }),
        getBoards: builder.query<
            Board[],
            {
                tenant: string;
                level?: number;
                parent?: number;
                teacherBoardTeacher?: string;
                boardEnrollStudent?: string;
                teacher_board_teacher?: string;
                board_enroll_student?: string;
            }
        >({
            query: ({
                tenant,
                level,
                parent,
                teacherBoardTeacher,
                boardEnrollStudent,
                teacher_board_teacher,
                board_enroll_student,
            }) => {
                const teacherFilter = teacher_board_teacher ?? teacherBoardTeacher ?? '';
                const studentFilter = board_enroll_student ?? boardEnrollStudent ?? '';
                return {
                    url: COURSE_ROUTES.GET_BOARDS(tenant),
                    method: 'GET',
                    params: {
                        ...(level !== undefined ? { level } : {}),
                        ...(parent !== undefined ? { parent } : {}),
                        teacher_board_teacher: teacherFilter,
                        board_enroll_student: studentFilter,
                        teacher_board__teacher: teacherFilter,
                        board_enroll__student: studentFilter,
                    },
                };
            },
            providesTags: ['Boards'],
        }),
        getBoardDetail: builder.query<Board, { tenant: string; boardId: number }>({
            query: ({ tenant, boardId }) => ({
                url: COURSE_ROUTES.GET_BOARD_DETAIL(tenant, boardId),
                method: 'GET',
            }),
            providesTags: ['Boards', 'BoardPeople'],
        }),
        getBoardsTree: builder.query<
            BoardTreeNode[],
            {
                tenant: string;
                level: number;
                teacherBoardTeacher?: string;
                boardEnrollStudent?: string;
                teacher_board_teacher?: string;
                board_enroll_student?: string;
            }
        >({
            query: ({
                tenant,
                level,
                teacherBoardTeacher,
                boardEnrollStudent,
                teacher_board_teacher,
                board_enroll_student,
            }) => ({
                url: COURSE_ROUTES.GET_BOARDS_TREE(tenant),
                method: 'GET',
                params: {
                    level,
                    teacher_board_teacher:
                        teacher_board_teacher ?? teacherBoardTeacher ?? '',
                    board_enroll_student:
                        board_enroll_student ?? boardEnrollStudent ?? '',
                    teacher_board__teacher:
                        teacher_board_teacher ?? teacherBoardTeacher ?? '',
                    board_enroll__student:
                        board_enroll_student ?? boardEnrollStudent ?? '',
                },
            }),
            providesTags: ['Boards'],
        }),
        createBoard: builder.mutation<
            Board,
            { tenant: string; data: CreateBoardPayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.GET_BOARDS(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['Boards', 'Courses'],
        }),
        patchBoard: builder.mutation<
            Board,
            {
                tenant: string;
                boardId: number;
                data: Partial<CreateBoardPayload>;
            }
        >({
            query: ({ tenant, boardId, data }) => ({
                url: COURSE_ROUTES.UPDATE_BOARD(tenant, boardId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['Boards', 'Courses'],
        }),
        deleteBoard: builder.mutation<
            { success?: boolean },
            { tenant: string; boardId: number }
        >({
            query: ({ tenant, boardId }) => ({
                url: COURSE_ROUTES.DELETE_BOARD(tenant, boardId),
                method: 'DELETE',
            }),
            invalidatesTags: ['Boards', 'Courses'],
        }),
        getBoardTeachers: builder.query<
            BoardTeacher[],
            { tenant: string; boardId: number }
        >({
            query: ({ tenant, boardId }) => ({
                url: COURSE_ROUTES.GET_BOARD_TEACHERS(tenant),
                method: 'GET',
                params: { board: boardId },
            }),
            providesTags: ['BoardPeople'],
        }),
        createBoardTeachers: builder.mutation<
            BoardTeacher[],
            { tenant: string; data: AddBoardTeachersPayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.GET_BOARD_TEACHERS(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['BoardPeople'],
        }),
        patchBoardTeacher: builder.mutation<
            BoardTeacher,
            { tenant: string; boardTeacherId: number; data: { active: boolean; board: number } }
        >({
            query: ({ tenant, boardTeacherId, data }) => ({
                url: COURSE_ROUTES.PATCH_BOARD_TEACHER(tenant, boardTeacherId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['BoardPeople'],
        }),
        deleteBoardTeacher: builder.mutation<
            { success?: boolean },
            { tenant: string; boardTeacherId: number }
        >({
            query: ({ tenant, boardTeacherId }) => ({
                url: COURSE_ROUTES.DELETE_BOARD_TEACHER(tenant, boardTeacherId),
                method: 'DELETE',
            }),
            invalidatesTags: ['BoardPeople'],
        }),
        getBoardStudents: builder.query<
            EnrolledStudent[],
            { tenant: string; boardId: number }
        >({
            query: ({ tenant, boardId }) => ({
                url: COURSE_ROUTES.GET_ENROLL_STUDENTS(tenant),
                method: 'GET',
                params: { board: boardId, semester_course: '', student: '' },
            }),
            providesTags: ['BoardPeople'],
        }),
        createBoardStudents: builder.mutation<
            EnrolledStudent[],
            { tenant: string; data: EnrollStudentsPayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.GET_ENROLL_STUDENTS(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['BoardPeople'],
        }),
        patchBoardStudent: builder.mutation<
            EnrolledStudent,
            { tenant: string; enrollmentId: number; data: { active: boolean; board: number } }
        >({
            query: ({ tenant, enrollmentId, data }) => ({
                url: COURSE_ROUTES.PATCH_ENROLL_STUDENT(tenant, enrollmentId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['BoardPeople'],
        }),
        deleteBoardStudent: builder.mutation<
            { success?: boolean },
            { tenant: string; enrollmentId: number }
        >({
            query: ({ tenant, enrollmentId }) => ({
                url: COURSE_ROUTES.DELETE_ENROLL_STUDENT(tenant, enrollmentId),
                method: 'DELETE',
            }),
            invalidatesTags: ['BoardPeople'],
        }),
        getUsers: builder.query<User[], { tenant: string }>({
            query: ({ tenant }) => {
                return {
                    url: COURSE_ROUTES.GET_USERS(tenant),
                    method: 'GET',
                };
            },
            transformResponse: (response: User[]) => {
                // Include both Teacher and School Admin role variants.
                return response.filter((user) =>
                    user.user_in_client_roles.some((role) => {
                        const normalized = role.trim().toUpperCase().replace(/\s+/g, '_');
                        return normalized === 'TEACHER' || normalized === 'SCHOOL_ADMIN';
                    })
                );
            },
            providesTags: ['Users'],
        }),
        createCourse: builder.mutation<
            Course,
            { tenant: string; data: CreateCoursePayload }
        >({
            query: ({ tenant, data }) => {
                return {
                    url: COURSE_ROUTES.GET_COURSES(tenant),
                    method: 'POST',
                    data,
                };
            },
            invalidatesTags: ['Courses'],
        }),
        getCourseDetail: builder.query<Course, { tenant: string; courseId: number }>({
            query: ({ tenant, courseId }) => ({
                url: COURSE_ROUTES.GET_COURSE_DETAIL(tenant, courseId),
                method: 'GET',
            }),
            providesTags: ['CourseDetail'],
        }),
        getGradingStructures: builder.query<GradingStructureSchemeItem[], { tenant: string }>({
            query: ({ tenant }) => ({
                url: COURSE_ROUTES.GRADING_STRUCTURE(tenant),
                method: 'GET',
            }),
            providesTags: ['CourseGradingSchemes'],
        }),
        createGradingStructure: builder.mutation<
            GradingStructureSchemeItem,
            {
                tenant: string;
                data: {
                    grading_structure_name: string;
                    grading_structure: Array<{
                        grade: string;
                        lower_threshold: string | number;
                        upper_threshold: string | number;
                    }>;
                };
            }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.GRADING_STRUCTURE(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseGradingSchemes'],
        }),
        deleteGradingStructure: builder.mutation<
            { success?: boolean },
            { tenant: string; gradingStructureId: number }
        >({
            query: ({ tenant, gradingStructureId }) => ({
                url: COURSE_ROUTES.GRADING_STRUCTURE_DETAIL(tenant, gradingStructureId),
                method: 'DELETE',
            }),
            invalidatesTags: ['CourseGradingSchemes', 'CourseDetail'],
        }),
        patchCourse: builder.mutation<
            Course,
            {
                tenant: string;
                courseId: number;
                data: PatchCoursePayload | FormData;
            }
        >({
            query: ({ tenant, courseId, data }) => ({
                url: COURSE_ROUTES.GET_COURSE_DETAIL(tenant, courseId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseDetail', 'CourseSections'],
        }),
        publishCourse: builder.mutation<
            Course,
            { tenant: string; courseId: number; data: { id: string; active: boolean } }
        >({
            query: ({ tenant, courseId, data }) => ({
                url: COURSE_ROUTES.UPDATE_COURSE_STATUS(tenant, courseId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseDetail', 'Courses'],
        }),
        duplicateCourse: builder.mutation<
            { id?: number; course_id?: number; new_course_id?: number } & Record<string, unknown>,
            { tenant: string; old_course_id: number }
        >({
            query: ({ tenant, old_course_id }) => ({
                url: COURSE_ROUTES.DUPLICATE_COURSE(tenant),
                method: 'POST',
                data: { old_course_id },
            }),
            invalidatesTags: ['Courses'],
        }),
        deleteCourse: builder.mutation<
            { success?: boolean },
            { tenant: string; courseId: number }
        >({
            query: ({ tenant, courseId }) => ({
                url: COURSE_ROUTES.GET_COURSE_DETAIL(tenant, courseId),
                method: 'DELETE',
            }),
            invalidatesTags: ['Courses', 'CourseDetail', 'CourseSections'],
        }),
        getCourseTeachers: builder.query<
            CourseTeacher[],
            { tenant: string; courseId: number }
        >({
            query: ({ tenant, courseId }) => ({
                url: COURSE_ROUTES.GET_COURSE_TEACHERS(tenant),
                method: 'GET',
                params: { course: courseId },
            }),
            providesTags: ['CourseTeachers'],
        }),
        createCourseTeachers: builder.mutation<
            CourseTeacher[],
            { tenant: string; data: AddCourseTeachersPayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.GET_COURSE_TEACHERS(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseTeachers', 'CourseDetail'],
        }),
        patchCourseTeacher: builder.mutation<
            CourseTeacher,
            {
                tenant: string;
                teacherMappingId: number;
                data: { active: boolean; semester_course: number };
            }
        >({
            query: ({ tenant, teacherMappingId, data }) => ({
                url: COURSE_ROUTES.PATCH_COURSE_TEACHER(tenant, teacherMappingId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseTeachers', 'CourseDetail'],
        }),
        deleteCourseTeacher: builder.mutation<
            { success?: boolean },
            { tenant: string; teacherMappingId: number }
        >({
            query: ({ tenant, teacherMappingId }) => ({
                url: COURSE_ROUTES.DELETE_COURSE_TEACHER(tenant, teacherMappingId),
                method: 'DELETE',
            }),
            invalidatesTags: ['CourseTeachers', 'CourseDetail'],
        }),
        getCourseSections: builder.query<
            CourseSection[],
            { tenant: string; courseId: number }
        >({
            query: ({ tenant, courseId }) => ({
                url: COURSE_ROUTES.GET_COURSE_SECTIONS(tenant),
                method: 'GET',
                params: { course: courseId },
            }),
            providesTags: ['CourseSections'],
        }),
        getLmsRepo: builder.query<
            CourseSectionRepoItem[],
            { tenant: string; courseId: number; courseSectionId: number }
        >({
            query: ({ tenant, courseId, courseSectionId }) => ({
                url: COURSE_ROUTES.GET_LMS_REPO(tenant),
                method: 'GET',
                params: {
                    course: courseId,
                    course_section: courseSectionId,
                },
            }),
            providesTags: ['CourseSections'],
        }),
        getAssignments: builder.query<
            AssignmentItem[],
            { tenant: string; courseId: number; courseSectionId?: number | null }
        >({
            query: ({ tenant, courseId, courseSectionId }) => ({
                url: COURSE_ROUTES.GET_ASSIGNMENTS(tenant),
                method: 'GET',
                params: {
                    course: courseId,
                    ...(courseSectionId !== undefined
                        ? { course_section: courseSectionId ?? '' }
                        : {}),
                },
            }),
            providesTags: ['CourseAssignments'],
        }),
        getAssignmentDetail: builder.query<AssignmentItem, { tenant: string; assignmentId: number }>({
            query: ({ tenant, assignmentId }) => ({
                url: COURSE_ROUTES.ASSIGNMENT_DETAIL(tenant, assignmentId),
                method: 'GET',
            }),
            providesTags: ['CourseAssignments'],
        }),
        getStudentSubmissions: builder.query<
            StudentSubmissionItem[],
            { tenant: string; assignmentId: number }
        >({
            query: ({ tenant, assignmentId }) => ({
                url: COURSE_ROUTES.GET_STUDENT_SUBMISSIONS(tenant),
                method: 'GET',
                params: { assignment: assignmentId },
            }),
            providesTags: ['CourseAssignments'],
        }),
        createStudentSubmission: builder.mutation<
            StudentSubmissionItem,
            { tenant: string; data: FormData }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.CREATE_STUDENT_SUBMISSION(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseAssignments'],
        }),
        createCourseFeedback: builder.mutation<
            { id?: number } & Record<string, unknown>,
            { tenant: string; data: CreateCourseFeedbackPayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.CREATE_COURSE_FEEDBACK(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseFeedbacks'],
        }),
        getCourseFeedbacks: builder.query<
            CourseFeedbackItem[],
            { tenant: string; courseId: number }
        >({
            query: ({ tenant, courseId }) => ({
                url: COURSE_ROUTES.CREATE_COURSE_FEEDBACK(tenant),
                method: 'GET',
                params: { course: courseId },
            }),
            providesTags: ['CourseFeedbacks'],
        }),
        patchCourseFeedback: builder.mutation<
            CourseFeedbackItem,
            {
                tenant: string;
                feedbackId: number;
                data: Partial<Pick<CourseFeedbackItem, 'feedback' | 'rating' | 'anonymous' | 'hidden'>>;
            }
        >({
            query: ({ tenant, feedbackId, data }) => ({
                url: COURSE_ROUTES.COURSE_FEEDBACK_DETAIL(tenant, feedbackId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseFeedbacks'],
        }),
        deleteCourseFeedback: builder.mutation<
            { success?: boolean },
            { tenant: string; feedbackId: number }
        >({
            query: ({ tenant, feedbackId }) => ({
                url: COURSE_ROUTES.COURSE_FEEDBACK_DETAIL(tenant, feedbackId),
                method: 'DELETE',
            }),
            invalidatesTags: ['CourseFeedbacks'],
        }),
        createStudentGroup: builder.mutation<
            { id?: number } & Record<string, unknown>,
            { tenant: string; data: CreateStudentGroupPayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.CREATE_STUDENT_GROUP(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseAssignments'],
        }),
        patchStudentGroup: builder.mutation<
            StudentGroupItem,
            { tenant: string; groupId: number; data: PatchStudentGroupPayload }
        >({
            query: ({ tenant, groupId, data }) => ({
                url: COURSE_ROUTES.STUDENT_GROUP_DETAIL(tenant, groupId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseAssignments'],
        }),
        deleteStudentGroup: builder.mutation<
            { success?: boolean },
            { tenant: string; groupId: number }
        >({
            query: ({ tenant, groupId }) => ({
                url: COURSE_ROUTES.STUDENT_GROUP_DETAIL(tenant, groupId),
                method: 'DELETE',
            }),
            invalidatesTags: ['CourseAssignments'],
        }),
        getStudentGroups: builder.query<
            StudentGroupItem[],
            { tenant: string; assignmentId: number; userId?: string }
        >({
            query: ({ tenant, assignmentId, userId }) => ({
                url: COURSE_ROUTES.CREATE_STUDENT_GROUP(tenant),
                method: 'GET',
                params: {
                    assignment: assignmentId,
                    ...(userId ? { users: userId } : {}),
                },
            }),
            providesTags: ['CourseAssignments'],
        }),
        getCourseAcquiredMarks: builder.query<
            CourseAcquiredMarksItem[],
            { tenant: string; markingScheme: number; courseId: number; student?: string }
        >({
            query: ({ tenant, markingScheme, courseId, student = '' }) => ({
                url: COURSE_ROUTES.GET_COURSE_ACQUIRED_MARKS(tenant),
                method: 'GET',
                params: {
                    marking_scheme: markingScheme,
                    course: courseId,
                    student,
                },
            }),
            providesTags: ['CourseAssignments'],
        }),
        createMultipleCourseAcquiredMarks: builder.mutation<
            { success?: boolean } | CourseAcquiredMarksItem[],
            { tenant: string; data: MultipleCourseAcquiredMarksPayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.CREATE_MULTIPLE_COURSE_ACQUIRED_MARKS(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseAssignments'],
        }),
        updateMultipleCourseAcquiredMarks: builder.mutation<
            { success?: boolean } | CourseAcquiredMarksItem[],
            { tenant: string; data: MultipleCourseAcquiredMarksPayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.UPDATE_MULTIPLE_COURSE_ACQUIRED_MARKS(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseAssignments'],
        }),
        patchCourseAcquiredMarks: builder.mutation<
            CourseAcquiredMarksItem,
            { tenant: string; acquiredMarkId: number; data: { marks: number } }
        >({
            query: ({ tenant, acquiredMarkId, data }) => ({
                url: COURSE_ROUTES.COURSE_ACQUIRED_MARKS_DETAIL(tenant, acquiredMarkId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseAssignments', 'CourseDetail'],
        }),
        getCourseMarkingScheme: builder.query<
            CourseMarkingSchemeItem[],
            { tenant: string; courseId: number }
        >({
            query: ({ tenant, courseId }) => ({
                url: COURSE_ROUTES.GET_COURSE_MARKING_SCHEME(tenant),
                method: 'GET',
                params: { course: courseId },
            }),
            providesTags: ['CourseDetail'],
        }),
        getCourseStructures: builder.query<CourseStructureItem[], { tenant: string }>({
            query: ({ tenant }) => ({
                url: COURSE_ROUTES.GET_COURSE_STRUCTURES(tenant),
                method: 'GET',
            }),
            providesTags: ['CourseDetail'],
        }),
        createCourseStructure: builder.mutation<
            CourseStructureItem,
            {
                tenant: string;
                data: {
                    assessment_type: string;
                    description: string;
                };
            }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.GET_COURSE_STRUCTURES(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseDetail'],
        }),
        patchCourseMarkingScheme: builder.mutation<
            CourseMarkingSchemeItem,
            {
                tenant: string;
                markingSchemeId: number;
                data: {
                    marking_structure_name: string;
                    course_percentage: string | number;
                    full_marks?: string | number;
                };
            }
        >({
            query: ({ tenant, markingSchemeId, data }) => ({
                url: COURSE_ROUTES.COURSE_MARKING_SCHEME_DETAIL(tenant, markingSchemeId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseDetail'],
        }),
        createCourseMarkingScheme: builder.mutation<
            CourseMarkingSchemeItem,
            {
                tenant: string;
                data: {
                    marking_structure_name: string;
                    description: string;
                    course_percentage: number;
                    full_marks: number;
                    course: number;
                    course_structure: number;
                    course_sub_structure?: number | null;
                };
            }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.GET_COURSE_MARKING_SCHEME(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseDetail'],
        }),
        deleteCourseMarkingScheme: builder.mutation<
            { success?: boolean },
            { tenant: string; markingSchemeId: number }
        >({
            query: ({ tenant, markingSchemeId }) => ({
                url: COURSE_ROUTES.COURSE_MARKING_SCHEME_DETAIL(tenant, markingSchemeId),
                method: 'DELETE',
            }),
            invalidatesTags: ['CourseDetail'],
        }),
        getCourseReport: builder.query<
            CourseReportItem[],
            { tenant: string; courseId: number; studentId?: string }
        >({
            query: ({ tenant, courseId, studentId = '' }) => ({
                url: COURSE_ROUTES.GET_COURSE_REPORT(tenant),
                method: 'GET',
                params: {
                    course: courseId,
                    course_marks__student: studentId,
                },
            }),
            providesTags: ['CourseDetail'],
        }),
        getEnrolledStudents: builder.query<
            EnrolledStudent[],
            {
                tenant: string;
                courseId?: number | '';
                board?: number | '';
                student?: string;
            }
        >({
            query: ({ tenant, courseId = '', board = '', student = '' }) => ({
                url: COURSE_ROUTES.GET_ENROLL_STUDENTS(tenant),
                method: 'GET',
                params: {
                    board,
                    semester_course: courseId,
                    student,
                },
            }),
            providesTags: ['CourseEnrollments'],
        }),
        createEnrolledStudents: builder.mutation<
            EnrolledStudent[],
            { tenant: string; data: EnrollStudentsPayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.GET_ENROLL_STUDENTS(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseEnrollments', 'CourseAttendance', 'CourseDetail'],
        }),
        patchEnrolledStudent: builder.mutation<
            EnrolledStudent,
            {
                tenant: string;
                enrollmentId: number;
                courseId: number;
                data: { active: boolean; semester_course: number };
            }
        >({
            query: ({ tenant, enrollmentId, courseId, data }) => ({
                url: COURSE_ROUTES.PATCH_ENROLL_STUDENT(tenant, enrollmentId),
                method: 'PATCH',
                params: { semester_course: courseId },
                data,
            }),
            invalidatesTags: ['CourseEnrollments', 'CourseAttendance'],
        }),
        deleteEnrolledStudent: builder.mutation<
            { success?: boolean },
            { tenant: string; enrollmentId: number; courseId: number }
        >({
            query: ({ tenant, enrollmentId, courseId }) => ({
                url: COURSE_ROUTES.DELETE_ENROLL_STUDENT(tenant, enrollmentId),
                method: 'DELETE',
                params: { semester_course: courseId },
            }),
            invalidatesTags: ['CourseEnrollments', 'CourseAttendance', 'CourseDetail'],
        }),
        getStudentAttendance: builder.query<
            EnrollStudentAttendance[],
            { tenant: string; courseId: number }
        >({
            query: ({ tenant, courseId }) => ({
                url: COURSE_ROUTES.GET_STUDENT_ATTENDANCE_PERCENTAGE(tenant),
                method: 'GET',
                params: { course: courseId },
            }),
            providesTags: ['CourseAttendance'],
        }),
        getStudentAttendancePercentageSummary: builder.query<
            StudentAttendancePercentageSummary | null,
            { tenant: string; courseId: number; studentId: string }
        >({
            query: ({ tenant, courseId, studentId }) => ({
                url: COURSE_ROUTES.GET_STUDENT_ATTENDANCE_PERCENTAGE(tenant),
                method: 'GET',
                params: { course: courseId, student: studentId },
            }),
            transformResponse: (
                response: StudentAttendancePercentageSummary | StudentAttendancePercentageSummary[]
            ) => {
                if (Array.isArray(response)) {
                    return response[0] ?? null;
                }
                return response ?? null;
            },
            providesTags: ['CourseAttendance'],
        }),
        getStudentsAttendanceByDate: builder.query<
            StudentAttendanceRecord[],
            { tenant: string; courseId: number; attendanceDate: string }
        >({
            query: ({ tenant, courseId, attendanceDate }) => ({
                url: COURSE_ROUTES.GET_STUDENTS_ATTENDANCE(tenant),
                method: 'GET',
                params: {
                    attendance_date: attendanceDate,
                    course: courseId,
                },
            }),
            providesTags: ['CourseAttendance'],
        }),
        createStudentsAttendance: builder.mutation<
            { success?: boolean },
            { tenant: string; courseId: number; data: CreateAttendancePayload }
        >({
            query: ({ tenant, courseId, data }) => ({
                url: COURSE_ROUTES.GET_STUDENTS_ATTENDANCE(tenant),
                method: 'POST',
                params: { course: courseId },
                data,
            }),
            invalidatesTags: ['CourseAttendance'],
        }),
        createMultiCourseLeaveRequest: builder.mutation<
            { success?: boolean },
            {
                tenant: string;
                data: FormData;
            }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.CREATE_MULTI_COURSE_LEAVE_REQUEST(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseAttendance'],
        }),
        updateStudentsAttendance: builder.mutation<
            { success?: boolean },
            { tenant: string; data: UpdateAttendancePayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.UPDATE_STUDENTS_ATTENDANCE(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseAttendance'],
        }),
        getRequestLeaveByDate: builder.query<
            LeaveRequestRecord[],
            { tenant: string; courseId: number; dateInRange: string }
        >({
            query: ({ tenant, courseId, dateInRange }) => ({
                url: COURSE_ROUTES.GET_REQUEST_LEAVE(tenant),
                method: 'GET',
                params: {
                    date_in_range: dateInRange,
                    course: courseId,
                },
            }),
            providesTags: ['CourseAttendance'],
        }),
        getRequestLeaveByStudent: builder.query<
            LeaveRequestRecord[],
            { tenant: string; courseId: number; studentId: string }
        >({
            query: ({ tenant, courseId, studentId }) => ({
                url: COURSE_ROUTES.GET_REQUEST_LEAVE(tenant),
                method: 'GET',
                params: {
                    student: studentId,
                    course: courseId,
                },
            }),
            providesTags: ['CourseAttendance'],
        }),
        getYearlyCourseAttendanceReport: builder.query<
            StudentYearlyAttendanceReportItem[],
            { tenant: string; courseId: number; studentId: string; year: number }
        >({
            query: ({ tenant, courseId, studentId, year }) => ({
                url: COURSE_ROUTES.GET_YEARLY_COURSE_ATTENDANCE_REPORT(tenant),
                method: 'GET',
                params: {
                    course: courseId,
                    student: studentId,
                    year,
                },
            }),
            providesTags: ['CourseAttendance'],
        }),
        getStudentCourseEvaluationReport: builder.query<
            StudentCourseEvaluationReportItem[],
            { tenant: string; courseId: number; studentId: string }
        >({
            query: ({ tenant, courseId, studentId }) => ({
                url: COURSE_ROUTES.GET_STUDENT_COURSE_EVALUATION_REPORT(tenant),
                method: 'GET',
                params: {
                    user: studentId,
                    user_skills_evaluation__evaluation_schema: tenant,
                    user_skills_evaluation__evaluation_course: courseId,
                },
            }),
            providesTags: ['CourseDetail'],
        }),
        getStudentCourseEvaluationReportByAssignment: builder.query<
            StudentCourseEvaluationReportItem[],
            { tenant: string; courseId: number; assignmentId: number; studentId: string }
        >({
            query: ({ tenant, courseId, assignmentId, studentId }) => ({
                url: COURSE_ROUTES.GET_STUDENT_COURSE_EVALUATION_REPORT(tenant),
                method: 'GET',
                params: {
                    user: studentId,
                    user_skills_evaluation__evaluation_schema: tenant,
                    user_skills_evaluation__evaluation_course: courseId,
                    user_skills_evaluation__evaluation_assignment: assignmentId,
                },
            }),
            providesTags: ['CourseDetail'],
        }),
        getStudentCourseEvaluationReportSelf: builder.query<
            StudentCourseEvaluationReportItem[],
            { tenant: string; courseId: number; assignmentId: number; studentId: string }
        >({
            query: ({ tenant, courseId, assignmentId, studentId }) => ({
                url: COURSE_ROUTES.GET_STUDENT_COURSE_EVALUATION_REPORT_SELF(tenant),
                method: 'GET',
                params: {
                    user: studentId,
                    user_skills_evaluation__evaluation_schema: tenant,
                    user_skills_evaluation__evaluation_course: courseId,
                    user_skills_evaluation__evaluation_assignment: assignmentId,
                },
            }),
            providesTags: ['CourseDetail'],
        }),
        getStudentCourseEvaluationReportPeer: builder.query<
            StudentCourseEvaluationReportItem[],
            { tenant: string; courseId: number; assignmentId: number; studentId: string }
        >({
            query: ({ tenant, courseId, assignmentId, studentId }) => ({
                url: COURSE_ROUTES.GET_STUDENT_COURSE_EVALUATION_REPORT_PEER(tenant),
                method: 'GET',
                params: {
                    user: studentId,
                    user_skills_evaluation__evaluation_schema: tenant,
                    user_skills_evaluation__evaluation_course: courseId,
                    user_skills_evaluation__evaluation_assignment: assignmentId,
                },
            }),
            providesTags: ['CourseDetail'],
        }),
        getStudentCourseEvaluationReportMentor: builder.query<
            StudentCourseEvaluationReportItem[],
            { tenant: string; courseId: number; assignmentId: number; studentId: string }
        >({
            query: ({ tenant, courseId, assignmentId, studentId }) => ({
                url: COURSE_ROUTES.GET_STUDENT_COURSE_EVALUATION_REPORT_MENTOR(tenant),
                method: 'GET',
                params: {
                    user: studentId,
                    user_skills_evaluation__evaluation_schema: tenant,
                    user_skills_evaluation__evaluation_course: courseId,
                    user_skills_evaluation__evaluation_assignment: assignmentId,
                },
            }),
            providesTags: ['CourseDetail'],
        }),
        getCourseSkillEvaluationListing: builder.query<
            CourseSkillEvaluationListingItem[],
            { tenant: string; courseId: number; assignmentId: number; userId: string }
        >({
            query: ({ tenant, courseId, assignmentId, userId }) => ({
                url: COURSE_ROUTES.COURSE_SKILL_EVALUATION_LISTING(tenant),
                method: 'GET',
                params: {
                    evaluation_course: courseId,
                    evaluation_assignment: assignmentId,
                    user: userId,
                },
            }),
            providesTags: ['CourseDetail'],
        }),
        getStudentAssignmentReport: builder.query<
            StudentAssignmentReportItem[],
            { tenant: string; courseId: number; studentId: string }
        >({
            query: ({ tenant, courseId, studentId }) => ({
                url: COURSE_ROUTES.GET_STUDENT_ASSIGNMENT_REPORT(tenant),
                method: 'GET',
                params: {
                    student: studentId,
                    semester_course: courseId,
                },
            }),
            providesTags: ['CourseDetail'],
        }),
        getCourseUserProfile: builder.query<
            CourseAnalyticsUserProfile,
            { tenant: string; userId: string }
        >({
            query: ({ tenant, userId }) => ({
                url: USER_ROUTES.USER_PROFILE_DETAIL(tenant, userId),
                method: 'GET',
            }),
            providesTags: ['CourseDetail'],
        }),
        patchAcceptLeave: builder.mutation<
            LeaveRequestRecord,
            {
                tenant: string;
                leaveId: number;
                data: {
                    request_status?: number;
                    remarks?: string;
                };
            }
        >({
            query: ({ tenant, leaveId, data }) => ({
                url: COURSE_ROUTES.PATCH_ACCEPT_LEAVE(tenant, leaveId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseAttendance'],
        }),
        createCourseSection: builder.mutation<
            CourseSection,
            { tenant: string; data: CreateCourseSectionPayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.GET_COURSE_SECTIONS(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseSections', 'CourseDetail'],
        }),
        deleteCourseSection: builder.mutation<
            { success?: boolean },
            { tenant: string; sectionId: number }
        >({
            query: ({ tenant, sectionId }) => ({
                url: COURSE_ROUTES.DELETE_COURSE_SECTION(tenant, sectionId),
                method: 'DELETE',
            }),
            invalidatesTags: ['CourseSections', 'CourseDetail'],
        }),
        patchCourseSection: builder.mutation<
            CourseSection,
            {
                tenant: string;
                sectionId: number;
                data: PatchCourseSectionPayload | FormData;
            }
        >({
            query: ({ tenant, sectionId, data }) => ({
                url: COURSE_ROUTES.PATCH_COURSE_SECTION(tenant, sectionId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseSections', 'CourseDetail'],
        }),
        patchLmsRepo: builder.mutation<
            CourseSectionRepoItem,
            {
                tenant: string;
                repoId: number;
                data: Pick<CourseSectionRepoItem, 'content' | 'name' | 'description'>;
            }
        >({
            query: ({ tenant, repoId, data }) => ({
                url: COURSE_ROUTES.PATCH_LMS_REPO(tenant, repoId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseSections', 'CourseDetail'],
        }),
        deleteLmsRepo: builder.mutation<
            { success?: boolean },
            { tenant: string; repoId: number }
        >({
            query: ({ tenant, repoId }) => ({
                url: COURSE_ROUTES.DELETE_LMS_REPO(tenant, repoId),
                method: 'DELETE',
            }),
            invalidatesTags: ['CourseSections', 'CourseDetail'],
        }),
        createCourseSubStructure: builder.mutation<
            { id: number; sub_structure_type: string; description: string; structure: number },
            {
                tenant: string;
                data: {
                    sub_structure_type: string;
                    description: string;
                    structure: number;
                };
            }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.CREATE_COURSE_SUB_STRUCTURE(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseDetail'],
        }),
        patchCourseSubStructure: builder.mutation<
            { id: number; sub_structure_type: string; description: string; structure: number },
            {
                tenant: string;
                subStructureId: number;
                data: {
                    sub_structure_type: string;
                    description: string;
                    structure: number;
                };
            }
        >({
            query: ({ tenant, subStructureId, data }) => ({
                url: COURSE_ROUTES.COURSE_SUB_STRUCTURE_DETAIL(tenant, subStructureId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseDetail'],
        }),
        createAssignment: builder.mutation<
            { id?: number } & Record<string, unknown>,
            { tenant: string; data: FormData }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.CREATE_ASSIGNMENT(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseSections', 'CourseDetail', 'CourseAssignments'],
        }),
        generateAssignmentIdeas: builder.query<
            GenerateAssignmentIdeasResponse,
            { tenant: string; courseId: number; sectionId?: number | null }
        >({
            query: ({ tenant, courseId, sectionId }) => ({
                url: COURSE_ROUTES.GENERATE_ASSIGNMENT_IDEAS(tenant),
                method: 'GET',
                params: {
                    course: courseId,
                    ...(sectionId ? { section: sectionId } : {}),
                },
            }),
        }),
        processCourseDocument: builder.mutation<
            ProcessCourseDocumentResponse,
            { tenant: string; data: FormData }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.PROCESS_COURSE_DOCUMENT(tenant),
                method: 'POST',
                data,
            }),
        }),
        generateStudyMaterials: builder.mutation<
            GenerateStudyMaterialsResponse,
            { tenant: string; data: { thread_id: string; chapters: { title: string }[] } }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.GENERATE_STUDY_MATERIALS(tenant),
                method: 'POST',
                data,
            }),
        }),
        evaluateCourseSkills: builder.mutation<
            { success?: boolean },
            { tenant: string; data: EvaluateCoursePayload }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.EVALUATE_COURSE(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseDetail'],
        }),
        patchAssignment: builder.mutation<
            { id?: number } & Record<string, unknown>,
            { tenant: string; assignmentId: number; data: FormData }
        >({
            query: ({ tenant, assignmentId, data }) => ({
                url: COURSE_ROUTES.ASSIGNMENT_DETAIL(tenant, assignmentId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseSections', 'CourseDetail', 'CourseAssignments'],
        }),
        deleteAssignment: builder.mutation<
            { success?: boolean },
            { tenant: string; assignmentId: number }
        >({
            query: ({ tenant, assignmentId }) => ({
                url: COURSE_ROUTES.ASSIGNMENT_DETAIL(tenant, assignmentId),
                method: 'DELETE',
            }),
            invalidatesTags: ['CourseSections', 'CourseDetail', 'CourseAssignments'],
        }),
        createStudentGrade: builder.mutation<
            SubmissionFeedbackItem,
            { tenant: string; data: { feedback: string; student_submission: number } }
        >({
            query: ({ tenant, data }) => ({
                url: COURSE_ROUTES.GRADE_STUDENTS(tenant),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['CourseAssignments'],
        }),
        getGradeStudentAssignment: builder.query<
            GradeStudentAssignmentResponse,
            { tenant: string; submissionId: number }
        >({
            query: ({ tenant, submissionId }) => ({
                url: COURSE_ROUTES.GRADE_STUDENT_ASSIGNMENT(tenant),
                method: 'GET',
                params: { submission_id: submissionId },
            }),
        }),
        patchStudentGrade: builder.mutation<
            SubmissionFeedbackItem,
            { tenant: string; gradeId: number; data: { feedback: string } }
        >({
            query: ({ tenant, gradeId, data }) => ({
                url: COURSE_ROUTES.GRADE_STUDENT_DETAIL(tenant, gradeId),
                method: 'PATCH',
                data,
            }),
            invalidatesTags: ['CourseAssignments'],
        }),
    }),
});

export const {
    useGetCoursesQuery,
    useGetBoardsQuery,
    useGetBoardDetailQuery,
    useGetBoardsTreeQuery,
    useCreateBoardMutation,
    usePatchBoardMutation,
    useDeleteBoardMutation,
    useGetBoardTeachersQuery,
    useCreateBoardTeachersMutation,
    usePatchBoardTeacherMutation,
    useDeleteBoardTeacherMutation,
    useGetBoardStudentsQuery,
    useCreateBoardStudentsMutation,
    usePatchBoardStudentMutation,
    useDeleteBoardStudentMutation,
    useGetUsersQuery,
    useCreateCourseMutation,
    useGetCourseDetailQuery,
    useGetGradingStructuresQuery,
    useCreateGradingStructureMutation,
    useDeleteGradingStructureMutation,
    usePatchCourseMutation,
    usePublishCourseMutation,
    useDuplicateCourseMutation,
    useDeleteCourseMutation,
    useGetCourseTeachersQuery,
    useCreateCourseTeachersMutation,
    usePatchCourseTeacherMutation,
    useDeleteCourseTeacherMutation,
    useGetCourseSectionsQuery,
    useGetLmsRepoQuery,
    useGetAssignmentsQuery,
    useGetAssignmentDetailQuery,
    useGetStudentSubmissionsQuery,
    useCreateStudentSubmissionMutation,
    useCreateCourseFeedbackMutation,
    useGetCourseFeedbacksQuery,
    usePatchCourseFeedbackMutation,
    useDeleteCourseFeedbackMutation,
    useCreateStudentGroupMutation,
    usePatchStudentGroupMutation,
    useDeleteStudentGroupMutation,
    useGetStudentGroupsQuery,
    useGetCourseAcquiredMarksQuery,
    useCreateMultipleCourseAcquiredMarksMutation,
    useUpdateMultipleCourseAcquiredMarksMutation,
    usePatchCourseAcquiredMarksMutation,
    useGetCourseMarkingSchemeQuery,
    useGetCourseStructuresQuery,
    useCreateCourseStructureMutation,
    usePatchCourseMarkingSchemeMutation,
    useCreateCourseMarkingSchemeMutation,
    useDeleteCourseMarkingSchemeMutation,
    useGetCourseReportQuery,
    useGetEnrolledStudentsQuery,
    useCreateEnrolledStudentsMutation,
    usePatchEnrolledStudentMutation,
    useDeleteEnrolledStudentMutation,
    useGetStudentAttendanceQuery,
    useGetStudentsAttendanceByDateQuery,
    useCreateStudentsAttendanceMutation,
    useCreateMultiCourseLeaveRequestMutation,
    useUpdateStudentsAttendanceMutation,
    useGetStudentAttendancePercentageSummaryQuery,
    useGetRequestLeaveByDateQuery,
    useGetRequestLeaveByStudentQuery,
    useGetYearlyCourseAttendanceReportQuery,
    useGetStudentCourseEvaluationReportQuery,
    useGetStudentCourseEvaluationReportByAssignmentQuery,
    useGetStudentCourseEvaluationReportSelfQuery,
    useGetStudentCourseEvaluationReportPeerQuery,
    useGetStudentCourseEvaluationReportMentorQuery,
    useGetCourseSkillEvaluationListingQuery,
    useGetStudentAssignmentReportQuery,
    useGetCourseUserProfileQuery,
    usePatchAcceptLeaveMutation,
    useCreateCourseSectionMutation,
    useDeleteCourseSectionMutation,
    usePatchCourseSectionMutation,
    usePatchLmsRepoMutation,
    useDeleteLmsRepoMutation,
    useCreateCourseSubStructureMutation,
    useGenerateAssignmentIdeasQuery,
    useLazyGenerateAssignmentIdeasQuery,
    useProcessCourseDocumentMutation,
    useGenerateStudyMaterialsMutation,
    useEvaluateCourseSkillsMutation,
    usePatchCourseSubStructureMutation,
    useCreateAssignmentMutation,
    usePatchAssignmentMutation,
    useDeleteAssignmentMutation,
    useCreateStudentGradeMutation,
    useLazyGetGradeStudentAssignmentQuery,
    usePatchStudentGradeMutation,
} = courseApi;
